import React, { useState, useEffect, useCallback} from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'react-hot-toast';
import { Loader2, ArrowLeft, FileQuestion, Package, CheckCircle2, Clock, XCircle, ArrowRight, FileText, Send, Edit, Truck, Phone, Mail } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const InquiryView = () => {
  const { inquiryNumber } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [inquiry, setInquiry] = useState(null);
  
  // Conversion form
  const [showConvertForm, setShowConvertForm] = useState(false);
  const [convertData, setConvertData] = useState({
    valid_from: new Date().toISOString().split('T')[0],
    valid_to: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    items_to_convert: [],
    pricing_procedure: 'ZSTD01'
  });


  const loadInquiry = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/presales/inquiries/${inquiryNumber}`);
      setInquiry(response.data);
      // Initialize items to convert
      setConvertData(prev => ({
        ...prev,
        items_to_convert: response.data.items?.map(i => i.item_number) || []
      }));
    } catch (error) {
      console.error('Error loading inquiry:', error);
      toast.error('Failed to load inquiry');
    } finally {
      setIsLoading(false);
    }
  }, [inquiryNumber]);

  useEffect(() => {
    loadInquiry();
  }, [loadInquiry]);

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel this inquiry?')) return;
    
    setIsProcessing(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/presales/inquiries/${inquiryNumber}/cancel`);
      setInquiry(response.data);
      toast.success('Inquiry cancelled');
    } catch (error) {
      console.error('Error cancelling inquiry:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel inquiry');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConvertToQuotation = async () => {
    if (convertData.items_to_convert.length === 0) {
      toast.error('Select at least one item to convert');
      return;
    }
    
    setIsProcessing(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/presales/inquiries/convert-to-quotation`, {
        inquiry_number: inquiryNumber,
        items_to_convert: convertData.items_to_convert,
        valid_from: convertData.valid_from,
        valid_to: convertData.valid_to,
        pricing_procedure: convertData.pricing_procedure
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        navigate(`/presales/quotations/${response.data.target_document}`);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      console.error('Error converting inquiry:', error);
      toast.error(error.response?.data?.detail || 'Failed to convert inquiry');
    } finally {
      setIsProcessing(false);
    }
  };

  const toggleItemSelection = (itemNumber) => {
    setConvertData(prev => ({
      ...prev,
      items_to_convert: prev.items_to_convert.includes(itemNumber)
        ? prev.items_to_convert.filter(n => n !== itemNumber)
        : [...prev.items_to_convert, itemNumber]
    }));
  };

  const getStatusBadge = (status) => {
    const badges = {
      'OPEN': { color: 'bg-orange-100 text-orange-800', icon: Clock },
      'IN_PROCESS': { color: 'bg-yellow-100 text-yellow-800', icon: ArrowRight },
      'CONVERTED': { color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
      'CANCELLED': { color: 'bg-red-100 text-red-800', icon: XCircle }
    };
    const badge = badges[status] || { color: 'bg-gray-100 text-gray-800', icon: FileQuestion };
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 text-sm font-medium rounded-full ${badge.color}`}>
        <Icon className="w-4 h-4" /> {status}
      </span>
    );
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
          <span className="ml-3">Loading inquiry...</span>
        </div>
      </Layout>
    );
  }

  if (!inquiry) {
    return (
      <Layout>
        <div className="p-6 text-center">
          <h1 className="text-xl font-bold text-gray-900 mb-4">Inquiry Not Found</h1>
          <Button onClick={() => navigate('/presales/inquiries')}>Back to Inquiries</Button>
        </div>
      </Layout>
    );
  }

  const canConvert = inquiry.status === 'OPEN' || inquiry.status === 'IN_PROCESS';

  return (
    <Layout>
      <div className="p-6" data-testid="inquiry-view">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/presales/inquiries')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <FileQuestion className="w-6 h-6" />
                Inquiry {inquiry.inquiry_number}
              </h1>
              <p className="text-sm text-gray-600">Document Type: {inquiry.document_type}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {getStatusBadge(inquiry.status)}
          </div>
        </div>

        {/* Status Flow & Actions */}
        <Card className="p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                ['OPEN', 'IN_PROCESS', 'CONVERTED'].includes(inquiry.status) ? 'bg-green-500 text-white' : 'bg-gray-200'
              }`}>1</span>
              <span className="text-sm font-medium">Open</span>
              <span className="mx-2 text-gray-300">→</span>
              
              <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                ['IN_PROCESS', 'CONVERTED'].includes(inquiry.status) ? 'bg-green-500 text-white' : 'bg-gray-200'
              }`}>2</span>
              <span className="text-sm font-medium">In Process</span>
              <span className="mx-2 text-gray-300">→</span>
              
              <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                inquiry.status === 'CONVERTED' ? 'bg-green-500 text-white' : 'bg-gray-200'
              }`}>3</span>
              <span className="text-sm font-medium">Converted to Quotation</span>
            </div>
            
            <div className="flex gap-2">
              {canConvert && (
                <Button variant="outline" onClick={() => navigate(`/presales/inquiries/${inquiryNumber}/edit`)} data-testid="edit-inquiry-btn">
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              )}
              {canConvert && !showConvertForm && (
                <Button onClick={() => setShowConvertForm(true)} data-testid="convert-to-quotation-btn">
                  <Send className="w-4 h-4 mr-2" />
                  Convert to Quotation
                </Button>
              )}
              {canConvert && (
                <Button variant="outline" className="text-red-600" onClick={handleCancel} disabled={isProcessing}>
                  <XCircle className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              )}
            </div>
          </div>
        </Card>

        {/* Convert to Quotation Form */}
        {showConvertForm && (
          <Card className="p-6 mb-6 border-2 border-orange-200 bg-orange-50">
            <h3 className="text-lg font-semibold mb-4">Convert to Quotation</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <Label>Quotation Valid From *</Label>
                <Input
                  type="date"
                  value={convertData.valid_from}
                  onChange={(e) => setConvertData({...convertData, valid_from: e.target.value})}
                />
              </div>
              <div>
                <Label>Quotation Valid To *</Label>
                <Input
                  type="date"
                  value={convertData.valid_to}
                  onChange={(e) => setConvertData({...convertData, valid_to: e.target.value})}
                />
              </div>
              <div>
                <Label>Pricing Procedure</Label>
                <Input value={convertData.pricing_procedure} disabled className="bg-gray-50" />
              </div>
            </div>
            
            <div className="mb-4">
              <Label>Select Items to Convert</Label>
              <div className="mt-2 space-y-2 max-h-40 overflow-y-auto">
                {inquiry.items?.map((item) => {
                  const isConverted = item.converted_quantity >= item.requested_quantity;
                  return (
                    <label key={item.item_number} className={`flex items-center gap-3 p-2 rounded ${isConverted ? 'bg-gray-100 opacity-50' : 'bg-white'}`}>
                      <input
                        type="checkbox"
                        checked={convertData.items_to_convert.includes(item.item_number)}
                        onChange={() => toggleItemSelection(item.item_number)}
                        disabled={isConverted}
                        className="w-4 h-4"
                      />
                      <span className="font-mono">{item.item_number.toString().padStart(4, '0')}</span>
                      <span className="flex-1">{item.item_description}</span>
                      <span className="font-mono">{item.requested_quantity} {item.unit_of_measure}</span>
                      {isConverted && <span className="text-xs text-green-600">Already converted</span>}
                    </label>
                  );
                })}
              </div>
            </div>
            
            <div className="flex gap-2">
              <Button onClick={handleConvertToQuotation} disabled={isProcessing}>
                {isProcessing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                Create Quotation
              </Button>
              <Button variant="outline" onClick={() => setShowConvertForm(false)}>Cancel</Button>
            </div>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Customer Info */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Customer Information</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Customer</span>
                <span className="font-medium">{inquiry.customer_id}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Name</span>
                <span className="font-medium">{inquiry.customer_name || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Sales Org</span>
                <span className="font-medium">{inquiry.sales_organization}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Dist. Channel</span>
                <span className="font-medium">{inquiry.distribution_channel}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Division</span>
                <span className="font-medium">{inquiry.division || '-'}</span>
              </div>
            </div>
          </Card>

          {/* Partner Functions & Commercial Terms */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Truck className="w-5 h-5" /> Partners & Terms
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Sold-to Party</span>
                <span className="font-medium">{inquiry.sold_to_party || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Ship-to Party</span>
                <span className="font-medium">{inquiry.ship_to_party || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Communication</span>
                <span className="font-medium flex items-center gap-1">
                  {inquiry.mode_of_communication === 'PHONE' && <Phone className="w-3 h-3" />}
                  {inquiry.mode_of_communication === 'EMAIL' && <Mail className="w-3 h-3" />}
                  {inquiry.mode_of_communication || '-'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Incoterms</span>
                <span className="font-medium">{inquiry.incoterms || '-'} {inquiry.incoterms_location ? `(${inquiry.incoterms_location})` : ''}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Payment Terms</span>
                <span className="font-medium">{inquiry.payment_terms || '-'}</span>
              </div>
            </div>
          </Card>

          {/* Dates */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold mb-4">Dates & Reference</h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Inquiry Date</span>
                <span className="font-medium">{inquiry.inquiry_date}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Pricing Date</span>
                <span className="font-medium">{inquiry.pricing_date || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Req. Delivery</span>
                <span className="font-medium">{inquiry.requested_delivery_date || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Valid From</span>
                <span className="font-medium">{inquiry.valid_from || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Valid To</span>
                <span className="font-medium">{inquiry.valid_to || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Order Reason</span>
                <span className="font-medium">{inquiry.order_reason || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Customer Ref</span>
                <span className="font-medium">{inquiry.customer_reference || '-'}</span>
              </div>
            </div>
          </Card>
        </div>

        {/* Document Flow */}
        <Card className="p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5" /> Document Flow
          </h3>
          <div>
            <span className="text-gray-600">Created Quotations:</span>
            {inquiry.quotation_numbers?.length > 0 ? (
              <div className="mt-2 flex flex-wrap gap-2">
                {inquiry.quotation_numbers.map(qt => (
                  <button
                    key={qt}
                    onClick={() => navigate(`/presales/quotations/${qt}`)}
                    className="font-mono text-green-600 hover:underline px-2 py-1 bg-green-50 rounded"
                  >
                    {qt}
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 mt-1">No quotations created yet</p>
            )}
          </div>
        </Card>

        {/* Items Table */}
        <Card className="overflow-hidden">
          <div className="px-6 py-4 border-b bg-gray-50">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Package className="w-5 h-5" /> Inquiry Items
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Item</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Material</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Description</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Req. Qty</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Plant</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Ship. Point</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Target Price</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Converted</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {inquiry.items?.map((item, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono">{item.item_number.toString().padStart(4, '0')}</td>
                    <td className="px-4 py-3 font-mono text-sm">{item.material_id || '-'}</td>
                    <td className="px-4 py-3">{item.item_description}</td>
                    <td className="px-4 py-3 text-right font-mono">
                      {item.requested_quantity} {item.unit_of_measure}
                    </td>
                    <td className="px-4 py-3 font-mono text-sm">
                      {item.plant || '-'}
                      {item.plant_determined_from && (
                        <span className="text-xs text-gray-400 block">
                          ({item.plant_determined_from === 'SHIP_TO_PARTY' ? 'Ship-to' : 
                            item.plant_determined_from === 'MATERIAL_MASTER' ? 'Material' : 'Manual'})
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-sm">{item.shipping_point || '-'}</td>
                    <td className="px-4 py-3 text-right font-mono">
                      {item.target_price ? `$${item.target_price.toFixed(2)}` : '-'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {item.converted_to_quotation ? (
                        <button
                          onClick={() => navigate(`/presales/quotations/${item.converted_to_quotation}`)}
                          className="text-green-600 hover:underline text-sm"
                        >
                          {item.converted_to_quotation}
                        </button>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Notes & Timestamps */}
        {inquiry.header_note && (
          <Card className="p-6 mt-6">
            <h3 className="text-lg font-semibold mb-2">Notes</h3>
            <p className="text-gray-700 whitespace-pre-wrap">{inquiry.header_note}</p>
          </Card>
        )}

        <div className="mt-6 text-sm text-gray-500">
          <p>Created: {inquiry.created_at ? new Date(inquiry.created_at).toLocaleString() : '-'}</p>
          <p>Updated: {inquiry.updated_at ? new Date(inquiry.updated_at).toLocaleString() : '-'}</p>
        </div>
      </div>
    </Layout>
  );
};

export default InquiryView;
